/**
 * 환경설정 &gt; 최근보낸메일 (migration 165, 2026-09-06 사용자 지시).
 *
 * 내가 최근에 보낸 주소 목록 + 전체삭제 + 줄마다 삭제.
 *
 * ★★★ **목록은 저장된 표가 아니다** — 보낸 메일에서 뽑아 낸다(수신자 자동완성이 보는 그 자리).
 *    그래서 **「삭제」는 메일을 지우는 것이 아니라 「앞으로 안 보이게 한다」**는 뜻이다.
 *    화면이 그 사실을 적는다 — 안 적으면 「보낸 메일이 지워지나?」를 묻게 된다.
 * ★★ 지운 주소는 **쓰기 화면의 받는사람 자동완성에서도 빠진다.** 목록에서 지웠는데 거기서
 *    계속 튀어나오면 지운 것이 아니다.
 * ★  목록은 **즉시 반영**이다 — 저장바를 두지 않는다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { hideMailRecentSent, listMailRecentSent, type MailRecentSent } from '../../api/mail';
import { Trash2 } from '../../icons';
import { Button } from '../../components/shared/Button';
import { Table, type TableColumn } from '../../components/shared/Table';
import { tMail } from '../../lib/mail-i18n';
import { MailConfirmDialog } from './MailConfirmDialog';

type Ask = { row: MailRecentSent } | { all: true };

/** 날짜만 적는다 — 「언제쯤 보냈나」를 알면 되지 시각까지는 필요 없다(정본 F18). */
function ymd(iso: string | null): string {
  if (!iso) return tMail('mail.recent.never');
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? tMail('mail.recent.never')
    : `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`;
}

export function MailSettingsRecent() {
  const [rows, setRows] = useState<MailRecentSent[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => { setRows(await listMailRecentSent()); }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .catch(() => { if (!cancelled) setNote({ ok: false, text: tMail('mail.recent.load_failed') }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const remove = useCallback(async (address?: string) => {
    setBusy(true);
    setNote(null);
    try {
      await hideMailRecentSent(address);
      await load();
      setNote({ ok: true, text: tMail('mail.recent.saved') });
      setAsk(null);
    } catch {
      setNote({ ok: false, text: tMail('mail.recent.failed') });
    } finally {
      setBusy(false);
    }
  }, [load]);

  const columns: ReadonlyArray<TableColumn<MailRecentSent>> = useMemo(() => [
    {
      key: 'address',
      header: tMail('mail.recent.col.address'),
      rowHeader: true,
      render: (r) => (
        <span className="filtrule__name">
          <span className="filtrule__nm">{r.address}</span>
          {/* 이름을 아는 주소면 함께 적는다 — 주소만으로는 누구인지 모른다. */}
          {r.name && <span className="spamrule__src">{r.name}</span>}
        </span>
      ),
    },
    {
      key: 'uses', header: tMail('mail.recent.col.uses'), numeric: true, width: '100px',
      render: (r) => <span>{r.uses}</span>,
    },
    {
      key: 'last', header: tMail('mail.recent.col.last'), width: '160px',
      render: (r) => <span>{ymd(r.lastAt)}</span>,
    },
    {
      key: 'manage', header: tMail('mail.recent.col.manage'), width: '96px',
      render: (r) => (
        <Button
          variant="quiet-danger" size="sm" disabled={busy}
          iconLeft={<Trash2 size={13} aria-hidden />}
          aria-label={tMail('mail.recent.delete_aria').replace('{n}', r.address)}
          onClick={() => setAsk({ row: r })}
        >
          {tMail('mail.recent.delete')}
        </Button>
      ),
    },
  ], [busy]);

  if (loading) return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;

  return (
    <div className="wc-set__body">
      <section className="wc-set__sec" aria-labelledby="mrec-h">
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id="mrec-h">{tMail('mail.recent.sec')}</h2>
          <Button
            variant="quiet-danger" size="sm"
            disabled={rows.length === 0 || busy}
            onClick={() => setAsk({ all: true })}
          >
            {tMail('mail.recent.clear')}
          </Button>
        </div>

        <Table
          caption={tMail('mail.recent.sec')}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.address}
          empty={<span>{tMail('mail.recent.none')}</span>}
          wrapClassName="mbox__tablewrap"
        />
        {/* 「지운다」가 무엇을 지우는지 — 안 적으면 보낸 메일이 지워지는 줄 안다. */}
        <p className="wc-set__hint">{tMail('mail.recent.hint')}</p>
        {note && <p className={note.ok ? 'wc-set__ok' : 'wc-set__bad'} role="status">{note.text}</p>}
      </section>

      <MailConfirmDialog
        open={ask !== null}
        danger
        busy={busy}
        title={ask && 'row' in ask
          ? tMail('mail.recent.del_title').replace('{n}', ask.row.address)
          : tMail('mail.recent.clear_title')}
        description={tMail('mail.recent.del_desc')}
        confirmLabel={tMail('mail.recent.delete')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          const cur = ask;
          if (!cur) return;
          void remove('row' in cur ? cur.row.address : undefined);
        }}
      />
    </div>
  );
}
